import { defineChatActions } from './actionsContext'
import { checkAuth, globalUnreadMessages } from './runtime'
import { playChatNewMessageSound } from '@/modules/chat-matrix-fast/utils/chatMessageSound'
import { showChatNewMessageToast } from '@/modules/chat-matrix-fast/utils/chatMessageToast'
import { isChatBeingViewed } from '@/modules/chat-matrix-fast/utils/chatSurface'
import { logger } from '@/utils/eventLogger'
import type {
  IChat,
  IChatTopic,
  IMessage,
} from '@/modules/chat-matrix-fast/types/IChat'
import { isEventChatItem } from '@/modules/chat-matrix-fast/constants/chat'
import {
  buildTopicsUnreadDebugBreakdown,
  computeTopicsGroupUnreadCount,
  countMatrixRoomUnread,
  countMatrixRoomUnreadRaw,
  findChatTopicByRoomId,
  forgetLocallyReadTopicRoom,
  getForceZeroTopicRoomIds,
  isTopicRoomLocallyRead,
  isTopicsModeChat,
  resolveActiveMessageRoomId,
  resolveTopicUnreadCounts,
  sameChatId,
} from '@/modules/chat-matrix-fast/utils/topics'
import {
  isMyMessage,
  useCurrentUser,
} from '@/modules/chat-matrix-fast/composables/useCurrentUser'
import {
  getMatrixRoomLastMessage,
  resolveMatrixRoomIdByChatId,
  roomIdToChatId,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import {
  getMatrixClient,
  startMatrixClient,
} from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { RoomEvent } from 'matrix-js-sdk'
import {
  isMatrixReadMarkerAtLastReadable,
  notifyMatrixReadReceiptError,
  sendMatrixReadReceiptForEvent,
} from '@/modules/chat-matrix-fast/services/matrixReadReceiptService'

import {
  notifyViewLastMessage,
  syncViewWithCore,
} from '@/modules/chat-matrix-fast/stores/chatStoreSync'
import { lastMessageDebugLog, summarizeLastMessageForLog, summarizeTopicForLog, topicsDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { logEventChat } from '@/modules/chat-matrix-fast/utils/eventChatDebug'
import {
  findMessage,
  isMessageAtOrBeforeReadMarker,
} from '@/modules/chat-matrix-fast/utils/messageIdentity'

let syncingTopicsUnreadFromMatrix = false
let matrixUnreadResyncAttached = false

function buildTopicUnreadForceZeroRoomIds(
  state: {
    activeTopic: IChatTopic | null
    currentChat: IChat | null
    showTopicsList?: boolean
  },
  chat: Pick<IChat, 'id' | 'matrix_room_id'>,
): Set<string> {
  const forceZeroRoomIds = getForceZeroTopicRoomIds()

  if (!state.currentChat || !sameChatId(state.currentChat.id, chat.id)) {
    return forceZeroRoomIds
  }

  const activeRoomId = resolveActiveMessageRoomId(state)?.trim()
  if (activeRoomId && countMatrixRoomUnreadRaw(activeRoomId) === 0) {
    forceZeroRoomIds.add(activeRoomId)
  }

  const parentRoomId = chat.matrix_room_id?.trim()
  if (parentRoomId && countMatrixRoomUnreadRaw(parentRoomId) === 0) {
    forceZeroRoomIds.add(parentRoomId)
  }

  return forceZeroRoomIds
}

function topicsUnreadCountsEqual(
  before: IChatTopic[] | undefined,
  after: IChatTopic[],
): boolean {
  const prev = before ?? []

  return after.every(topic => {
    const prevTopic = prev.find(item => item.id === topic.id)
    return (prevTopic?.unread_count ?? 0) === (topic.unread_count ?? 0)
  })
}

export const unreadActions = defineChatActions({
  updateTitleUnreadCount(): void {
    const totalUnread = this.chats.reduce(
      (total, chat) => total + (chat.unread_count || 0),
      0,
    )

    globalUnreadMessages.setUnreadCount(totalUnread)
  },

  // Обновляет превью чата при входящем Matrix-сообщении; звук — только при реальном росте unread
  handleMatrixIncomingMessage(
    roomId: string,
    message: IMessage,
    options?: { bumpUnread?: boolean },
  ): void {
    const bumpUnread = options?.bumpUnread !== false
    // Новое непрочитанное снимает optimistic overlay; live в открытой комнате — нет.
    if (bumpUnread) {
      forgetLocallyReadTopicRoom(roomId)
    }

    const topicMatch = findChatTopicByRoomId(this.chats, roomId)
    if (topicMatch) {
      const { chat, chatIndex, topicIndex } = topicMatch
      const topics = [...(chat.topics ?? [])]
      const topic = topics[topicIndex]
      const isActiveTopicRoom = isChatBeingViewed()
        && this.activeTopic?.matrix_room_id === roomId
      const prevUnread = topic.unread_count ?? 0
      const topicMatrixUnread = !bumpUnread || isActiveTopicRoom ? 0 : countMatrixRoomUnread(roomId)
      const nextUnread = !bumpUnread || isActiveTopicRoom
        ? prevUnread
        : Math.max(topicMatrixUnread, prevUnread > 0 ? prevUnread : 1)
      topics[topicIndex] = {
        ...topic,
        unread_count: nextUnread,
      }

      const updatedChats = [...this.chats]
      updatedChats[chatIndex] = {
        ...chat,
        topics,
        unread_count: topics.reduce((sum, item) => sum + (item.unread_count ?? 0), 0),
        last_message_id: message.id,
        last_matrix_event_id: message.matrix_event_id,
        last_message: message,
      }
      this.chats = updatedChats

      if (this.currentChat?.id === chat.id) {
        this.currentChat = updatedChats[chatIndex]
      }

      this.updateTitleUnreadCount()
      if (nextUnread > prevUnread) {
        playChatNewMessageSound(chat.id)
        showChatNewMessageToast(chat.id, message, { roomId, chat })
      }
      lastMessageDebugLog('handleMatrixIncomingMessage · topic room', {
        roomId,
        chatId: chat.id,
        bumpUnread,
        message: summarizeLastMessageForLog(message),
      })
      notifyViewLastMessage(chat.id, message)
      return
    }

    const chatId = this.resolveChatIdByMatrixRoomId(roomId)
    const chatIndex = this.chats.findIndex(chat => chat.matrix_room_id === roomId)

    if (chatIndex === -1) {
      if (bumpUnread && chatId) {
        showChatNewMessageToast(chatId, message, { roomId })
      }
      void this.fetchChats(undefined, { silent: true })
      return
    }

    const oldCount = this.chats[chatIndex].unread_count || 0
    const updatedChats = [...this.chats]
    const matrixUnread = bumpUnread ? countMatrixRoomUnread(roomId) : 0
    const nextUnread = bumpUnread
      ? Math.max(matrixUnread, oldCount > 0 ? oldCount : 1)
      : oldCount

    updatedChats[chatIndex] = {
      ...updatedChats[chatIndex],
      unread_count: nextUnread,
      last_message_id: message.id,
      last_matrix_event_id: message.matrix_event_id,
      last_message: message,
    }
    this.chats = updatedChats

    if (this.currentChat?.id === updatedChats[chatIndex].id) {
      this.currentChat = {
        ...this.currentChat,
        last_message: message,
        last_message_id: message.id,
        last_matrix_event_id: message.matrix_event_id,
      }
    }

    this.updateTitleUnreadCount()
    if (bumpUnread) {
      const chat = updatedChats[chatIndex]
      playChatNewMessageSound(chat.id)
      showChatNewMessageToast(chat.id, message, { roomId, chat })
    }
    lastMessageDebugLog('handleMatrixIncomingMessage · room', {
      roomId,
      chatId,
      bumpUnread,
      message: summarizeLastMessageForLog(message),
      coreChatLastMessage: summarizeLastMessageForLog(updatedChats[chatIndex].last_message),
    })
    logEventChat('incoming · message', updatedChats[chatIndex], {
      roomId,
      bumpUnread,
      message: summarizeLastMessageForLog(message),
    })
    notifyViewLastMessage(chatId ?? updatedChats[chatIndex].id, message)
  },

  async handleMatrixMessageRedaction(roomId: string, redactedEventId: string): Promise<void> {
    const chatIndex = this.chats.findIndex(chat => chat.matrix_room_id === roomId)
    if (chatIndex === -1) {
      return
    }

    const chatId = this.chats[chatIndex].id
    const isCurrentChat = this.currentChat?.matrix_room_id === roomId

    try {
      const matrixClient = await startMatrixClient()
      const room = matrixClient.getRoom(roomId)
      if (!room) {
        return
      }

      const lastMessage = getMatrixRoomLastMessage(room)
      const updatedChats = [...this.chats]
      const current = updatedChats[chatIndex]

      updatedChats[chatIndex] = {
        ...current,
        last_message: lastMessage,
        last_message_id: lastMessage?.id,
      }

      this.chats = updatedChats
      this.updateTitleUnreadCount()

      if (lastMessage) {
        notifyViewLastMessage(chatId, lastMessage)
      } else {
        syncViewWithCore()
      }

      if (!isCurrentChat) {
        this.syncMatrixUnreadForAllChats()
      }

      await sendMatrixReadReceiptForEvent(matrixClient, roomId, redactedEventId)
    } catch {
      // Не блокируем UI при ошибке синхронизации
    }

    if (isCurrentChat) {
      void this.acknowledgeMatrixReadReceipts()
    }
  },

  resolveChatIdByMatrixRoomId(roomId: string): number {
    return this.chats.find(chat => chat.matrix_room_id === roomId)?.id ?? roomIdToChatId(roomId)
  },

  async markChatAsRead(
    chatId: number,
    lastMessageId?: number,
    lastMatrixEventId?: string,
  ): Promise<void> {
    try {
      const chatIndex = this.chats.findIndex(c => c.id === chatId)
      const chat = chatIndex >= 0 ? this.chats[chatIndex] : this.currentChat
      // Явная комната чата — не active room: openExistingChat зовёт mark до смены currentChat.
      const roomId = (
        (isTopicsModeChat(chat) && this.activeTopic?.matrix_room_id)
        || chat?.matrix_room_id
        || resolveActiveMessageRoomId(this.$state)
      )?.trim() || null

      topicsDebugLog('core · markChatAsRead · start', {
        chatId,
        lastMessageId: lastMessageId ?? null,
        isCurrentChat: this.currentChat?.id === chatId,
        isTopicsGroup: isTopicsModeChat(chat),
        activeTopic: summarizeTopicForLog(this.activeTopic),
        activeRoomId: roomId,
        chatUnreadBefore: chat?.unread_count ?? 0,
        ...(chat && isTopicsModeChat(chat)
          ? { unreadBreakdownBefore: buildTopicsUnreadDebugBreakdown(chat) }
          : {}),
      })

      if (isTopicsModeChat(chat) && !this.activeTopic) {
        topicsDebugLog('core · markChatAsRead · skip (topics group, no active topic)', {
          chatId,
        })
        return
      }

      // unread_count сбрасываем только после подтверждения Matrix (acknowledge → applyLocalRead).
      const readMarkerMessage = lastMessageId != null || lastMatrixEventId
        ? findMessage(this.messages, {
          id: lastMessageId,
          matrixEventId: lastMatrixEventId,
        })
        : undefined
      const resolvedLastMessageId = lastMessageId ?? readMarkerMessage?.id
      const resolvedLastMatrixEventId = lastMatrixEventId?.trim()
        || readMarkerMessage?.matrix_event_id

      if (chatIndex !== -1 && resolvedLastMessageId) {
        const updatedChats = [...this.chats]
        updatedChats[chatIndex] = {
          ...updatedChats[chatIndex],
          last_read_message_id: resolvedLastMessageId,
          ...(resolvedLastMatrixEventId
            ? { last_read_matrix_event_id: resolvedLastMatrixEventId }
            : {}),
        }
        this.chats = updatedChats
      }

      if (this.currentChat?.id === chatId) {
        const currentUserInfo = useCurrentUser(this.currentChat)
        const currentUserId = String(currentUserInfo.id.value ?? '')
        const currentUserName = String(currentUserInfo.name.value ?? '')

        this.messages.forEach(message => {
          const isFromCurrentUser = isMyMessage(
            message,
            currentUserId,
            currentUserName,
            currentUserInfo.matrixUserId.value,
          )

          if (isFromCurrentUser) {
            return
          }

          if (
            isMessageAtOrBeforeReadMarker(
              message,
              resolvedLastMessageId,
              resolvedLastMatrixEventId,
              readMarkerMessage,
            )
          ) {
            message.is_read = true
          }
        })
      }

      if (roomId) {
        void this.acknowledgeMatrixReadReceipts(roomId)
      }

      topicsDebugLog('core · markChatAsRead · done', {
        chatId,
        activeRoomId: roomId,
        chatUnreadAfter: this.chats.find(c => c.id === chatId)?.unread_count ?? 0,
      })
    } catch (error) {
      topicsDebugLog('core · markChatAsRead · error', {
        chatId,
        error: String(error),
      })
    }
  },

  syncTopicsUnreadForAllGroups(): void {
    for (const chat of this.chats) {
      if (isTopicsModeChat(chat) && chat.topics?.length) {
        this.syncTopicsUnreadFromMatrix(chat.id)
      }
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('matrix-read-receipts-updated'))
    }
  },

  syncMatrixUnreadForAllChats(): void {
    let changed = false
    const forceZeroRoomIds = getForceZeroTopicRoomIds()
    const updatedChats = this.chats.map(chat => {
      if (isTopicsModeChat(chat)) {
        return chat
      }

      const roomId = chat.matrix_room_id?.trim()
      if (!roomId) {
        return chat
      }

      const unread = forceZeroRoomIds.has(roomId) || isTopicRoomLocallyRead(roomId)
        ? 0
        : countMatrixRoomUnread(roomId)
      if ((chat.unread_count ?? 0) === unread) {
        return chat
      }

      changed = true
      return { ...chat, unread_count: unread }
    })

    if (changed) {
      this.chats = updatedChats
      if (this.currentChat) {
        const current = updatedChats.find(item => sameChatId(item.id, this.currentChat!.id))
        if (current) {
          this.currentChat = current
        }
      }
      this.updateTitleUnreadCount()
      syncViewWithCore()
    }

    this.syncTopicsUnreadForAllGroups()
  },

  scheduleMatrixUnreadResync(): void {
    if (matrixUnreadResyncAttached || typeof window === 'undefined') {
      return
    }

    matrixUnreadResyncAttached = true

    let resyncTimer: number | undefined
    const scheduleResync = () => {
      window.clearTimeout(resyncTimer)
      resyncTimer = window.setTimeout(() => {
        this.syncMatrixUnreadForAllChats()
      }, 50)
    }

    void startMatrixClient().then(client => {
      const onSync = (state: string) => {
        if (state === 'SYNCING' || state === 'CATCHUP') {
          scheduleResync()
        }
      }

      client.on('sync' as any, onSync)
      client.on(RoomEvent.UnreadNotifications, scheduleResync)
      const currentState = client.getSyncState()
      if (currentState === 'SYNCING' || currentState === 'CATCHUP') {
        scheduleResync()
      }
    })
  },

  syncTopicsUnreadFromMatrix(chatId?: number | string): void {
    if (syncingTopicsUnreadFromMatrix) {
      return
    }

    const targetId = chatId ?? this.currentChat?.id
    if (targetId == null) {
      return
    }

    const chatIndex = this.chats.findIndex(chat => sameChatId(chat.id, targetId))
    const chat = chatIndex >= 0 ? this.chats[chatIndex] : this.currentChat
    if (!chat?.topics?.length) {
      return
    }

    syncingTopicsUnreadFromMatrix = true
    try {
      const forceZeroRoomIds = buildTopicUnreadForceZeroRoomIds(this.$state, chat)
      const topics = resolveTopicUnreadCounts(chat, {
        debugLabel: 'sync',
        forceZeroRoomIds,
      })
      const unread_count = computeTopicsGroupUnreadCount(chat, topics, forceZeroRoomIds)
      const breakdown = buildTopicsUnreadDebugBreakdown(chat, { resolvedTopics: topics })

      if (
        topicsUnreadCountsEqual(chat.topics, topics)
          && (chat.unread_count ?? 0) === unread_count
      ) {
        topicsDebugLog('core · syncTopicsUnreadFromMatrix · skip (unchanged)', {
          chatId: targetId,
          unread_count,
          breakdown,
        })
        return
      }

      const updatedChat = { ...chat, topics, unread_count }

      if (chatIndex >= 0) {
        const updatedChats = [...this.chats]
        updatedChats[chatIndex] = updatedChat
        this.chats = updatedChats
      }

      if (this.currentChat && sameChatId(this.currentChat.id, targetId)) {
        this.currentChat = updatedChat
      }

      this.updateTitleUnreadCount()
      syncViewWithCore()
      topicsDebugLog('core · syncTopicsUnreadFromMatrix · done', {
        chatId: targetId,
        parentUnread: chat.matrix_room_id ? countMatrixRoomUnread(chat.matrix_room_id) : 0,
        unread_count,
        topics: topics.map(topic => ({
          id: topic.id,
          title: topic.title,
          unread_count: topic.unread_count ?? 0,
          matrix_room_id: topic.matrix_room_id ?? null,
        })),
        breakdown,
      })
    } finally {
      syncingTopicsUnreadFromMatrix = false
    }
  },

  applyLocalReadForRoom(roomId: string): void {
    const matrixUnreadBefore = countMatrixRoomUnreadRaw(roomId)
    const matrixClient = getMatrixClient()
    const room = matrixClient?.getRoom(roomId)
    const userId = matrixClient?.getUserId() ?? ''
    const markerOk = Boolean(room && userId && isMatrixReadMarkerAtLastReadable(room, userId))
    const locallyRead = isTopicRoomLocallyRead(roomId)

    topicsDebugLog('core · applyLocalReadForRoom · start', {
      roomId,
      matrixUnreadBefore,
      markerOk,
      locallyRead,
    })

    // Пока ACK в полёте — locallyRead держит UI на нуле; иначе ждём marker/raw.
    if (matrixUnreadBefore > 0 && !markerOk && !locallyRead) {
      topicsDebugLog('core · applyLocalReadForRoom · skip (matrix unread)', {
        roomId,
        matrixUnreadBefore,
      })
      return
    }

    if (matrixUnreadBefore === 0) {
      forgetLocallyReadTopicRoom(roomId)
    }

    const topicMatch = findChatTopicByRoomId(this.chats, roomId)

    if (topicMatch) {
      const { chat, chatIndex, topicIndex } = topicMatch
      const topicBefore = chat.topics?.[topicIndex]
      const breakdownBefore = buildTopicsUnreadDebugBreakdown(chat, {
        isActiveTopicId: this.activeTopic?.id ?? null,
      })

      const forceZeroRoomIds = buildTopicUnreadForceZeroRoomIds(this.$state, chat)
      forceZeroRoomIds.add(roomId.trim())
      const topics = resolveTopicUnreadCounts(chat, {
        debugLabel: 'applyLocalReadForRoom',
        forceZeroRoomIds,
      }).map(topic =>
        topic.matrix_room_id?.trim() === roomId.trim()
          ? { ...topic, unread_count: 0 }
          : topic,
      )
      const unread_count = computeTopicsGroupUnreadCount(chat, topics, forceZeroRoomIds)
      const breakdownAfter = buildTopicsUnreadDebugBreakdown(
        { ...chat, topics, unread_count },
        { resolvedTopics: topics, isActiveTopicId: this.activeTopic?.id ?? null },
      )
      const updatedChat = { ...chat, topics, unread_count }
      const updatedChats = [...this.chats]
      updatedChats[chatIndex] = updatedChat
      this.chats = updatedChats

      if (this.currentChat?.id === chat.id) {
        this.currentChat = updatedChat
      }

      this.updateTitleUnreadCount()
      syncViewWithCore()

      topicsDebugLog('core · applyLocalReadForRoom · topic room', {
        roomId,
        chatId: chat.id,
        topicId: topicBefore?.id ?? null,
        topicTitle: topicBefore?.title ?? null,
        matrixUnreadBefore,
        matrixUnreadAfter: countMatrixRoomUnreadRaw(roomId),
        manuallyZeroedTopicId: topicBefore?.id ?? null,
        unread_count,
        breakdownBefore,
        breakdownAfter,
        note: countMatrixRoomUnreadRaw(roomId) > 0
          ? 'Matrix still reports unread — local zero only because read marker caught up'
          : 'Local unread cleared',
      })
      return
    }

    const chatIndex = this.chats.findIndex(chat => chat.matrix_room_id === roomId)
    if (chatIndex === -1) {
      topicsDebugLog('core · applyLocalReadForRoom · skip (chat not found)', { roomId })
      return
    }

    const updatedChats = [...this.chats]
    updatedChats[chatIndex] = {
      ...updatedChats[chatIndex],
      unread_count: 0,
    }
    this.chats = updatedChats

    if (this.currentChat?.matrix_room_id === roomId) {
      this.currentChat = {
        ...this.currentChat,
        unread_count: 0,
      }
    }

    this.updateTitleUnreadCount()
    syncViewWithCore()

    topicsDebugLog('core · applyLocalReadForRoom · parent room', {
      roomId,
      chatId: updatedChats[chatIndex].id,
      matrixUnreadBefore,
      matrixUnreadAfter: countMatrixRoomUnreadRaw(roomId),
    })
  },

  async markChatAsReadOnServer(chatId: number): Promise<void> {
    if (!checkAuth()) {
      return
    }

    const chat = this.chats.find(c => c.id === chatId) ?? this.currentChat
    let roomId = resolveActiveMessageRoomId(this.$state)

    if (!roomId) {
      roomId = chat?.matrix_room_id
    }

    if (!roomId) {
      roomId = await resolveMatrixRoomIdByChatId(chatId)
    }

    if (roomId) {
      try {
        await this.acknowledgeMatrixReadReceipts(roomId)
      } catch (error) {
        logger.error('chat_markChatAsReadOnServer_error', {
          file: 'chatStore',
          function: 'markChatAsReadOnServer',
          condition: String(error),
        })
        notifyMatrixReadReceiptError(error, roomId)
        syncViewWithCore()
      }
    }
  },
})
